import styles from "./CommentItem.module.css"

import IconLockSquare from "@shared/components/icons/LockSquare"
import IconTrash from "@shared/components/icons/Trash"
import { useDocument, useDocumentsTogether } from "@shared/components/providers/SupergrainProvider"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import {
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "@shared/components/untitledui/modal"
import type { Comment } from "@shared/models/comment"
import { tracked } from "@supergrain/kernel/react"
import DOMPurify from "dompurify"
import { useState } from "react"
import { Button as AriaButton } from "react-aria-components"

interface Props {
  comment: Comment
  currentUserId: string | null
  /**
   * Soft-deletes the comment. When provided and the user has can-edit-comment, a delete
   * button and confirmation dialog render.
   */
  deleteComment?: (commentId: string) => void
}

/**
 * A single comment row, shared by CardComments and LessonComments: its HTML body, the
 * commenter's name and date, a "Visible to …" badge for private comments, and — when
 * `deleteComment` is supplied — a delete button with a confirmation dialog.
 */
export default tracked(function CommentItem({ comment, currentUserId, deleteComment }: Props) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const commenter = useDocument("user-summary", comment.attributes.commenterId).value
  const commenterName = commenter
    ? `${commenter.attributes.firstName} ${commenter.attributes.lastName}`
    : ""

  const insertedAt = comment.attributes.insertedAt
  const formattedDate = insertedAt
    ? new Date(insertedAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : ""

  // A comment is private when it isn't shared with the whole planbook.
  const recipients = comment.attributes.recipients
  const isPrivate = !recipients.some((recipient) => recipient.type === "planbook")
  const userRecipientIds = recipients
    .filter((recipient) => recipient.type === "user")
    .map((recipient) => recipient.id)
  const isRecipient = currentUserId != null && userRecipientIds.includes(currentUserId)
  const otherRecipientIds = userRecipientIds.filter((id) => id !== currentUserId)

  const canDelete = deleteComment && comment.meta.permissions.includes("can-edit-comment")

  return (
    <div className={`${styles.item} test-comment-item`}>
      <div>
        <span
          className={styles.itemText}
          data-test-comment-body
          // Comment text is stored as HTML.
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(comment.attributes.commentText) }}
        />
        <span>
          {commenterName}
          {formattedDate && ` ${formattedDate}`}
        </span>
        {isPrivate && (
          <CommentVisibility isRecipient={isRecipient} otherRecipientIds={otherRecipientIds} />
        )}
      </div>
      {canDelete && (
        <AriaButton
          className={`${styles.removeCommentBtn} test-delete-comment`}
          onPress={() => setShowDeleteDialog(true)}
          aria-label="Delete comment"
          data-test-delete-comment-button
        >
          <IconTrash />
        </AriaButton>
      )}
      {deleteComment && (
        <DeleteCommentDialog
          open={showDeleteDialog}
          onConfirm={() => {
            deleteComment(comment.id)
            setShowDeleteDialog(false)
          }}
          onDismiss={() => setShowDeleteDialog(false)}
        />
      )}
    </div>
  )
})

const CommentVisibility = tracked(function CommentVisibility({
  isRecipient,
  otherRecipientIds,
}: {
  isRecipient: boolean
  otherRecipientIds: string[]
}) {
  const otherRecipients = useDocumentsTogether("user-summary", otherRecipientIds).value ?? []
  const otherRecipientNamesString = otherRecipients
    .map((user) => `${user.attributes.firstName} ${user.attributes.lastName}`)
    .join(", ")

  // Whom the comment is visible to, always including admins. Mirrors the legacy phrasing.
  const audience = isRecipient ? "you, admins," : "admins"

  return (
    <div className={styles.visibility}>
      <IconLockSquare className="fill-current" />
      <span>
        Visible to{" "}
        {otherRecipientIds.length === 0 ? (
          isRecipient ? (
            "you and admins"
          ) : (
            "admins"
          )
        ) : otherRecipientIds.length === 1 ? (
          `${audience} and ${otherRecipientNamesString}`
        ) : (
          <>
            {audience} and{" "}
            <span className={styles.recipientsHover} title={otherRecipientNamesString}>
              {otherRecipientIds.length} others
            </span>
          </>
        )}
      </span>
    </div>
  )
})

function DeleteCommentDialog({
  open,
  onConfirm,
  onDismiss,
}: {
  open: boolean
  onConfirm: () => void
  onDismiss: () => void
}) {
  return (
    <ModalContent
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onDismiss()
      }}
      role="alertdialog"
      size="sm"
    >
      <ModalHeader>
        <ModalTitle>Delete Comment?</ModalTitle>
        <ModalDescription>Are you sure you want to delete this comment?</ModalDescription>
      </ModalHeader>
      <ModalFooter>
        <ModalClose color="secondary" onPress={onDismiss}>
          Cancel
        </ModalClose>
        <Button color="primary-destructive" onPress={onConfirm}>
          Delete
        </Button>
      </ModalFooter>
    </ModalContent>
  )
}
